import { useRouter } from 'next/dist/client/router';
import { LOGO_MANFRED_WHITE } from 'utils/logos';
import {
  WP_REST_API_Post,
  WP_REST_API_User,
  WP_REST_API_Attachment,
  WP_REST_API_Categories
} from 'wp-types';
import { CategoryBadge } from '../cards/CategoryBadge';
import {
  StyledPostHeader,
  StyledHeaderContent,
  StyledHeaderTitle,
  StyledHeaderMetaTable,
  StyledHeaderThumb,
  StyledHeaderAuthorName,
  StyledHeaderTags,
  AuthorEntry,
  PublishedEntry,
  UpdatedEntry,
  ReadTimeEntry,
  MetaAvatar,
  MetaText,
  MetaTitle,
  MetaValue
} from './PostHeaderCard.styled';
import { computeReadTime } from 'utils/computeReadTime';
import { useI18n } from 'hooks/useI18n';
import { getPostCategoriesId } from '../utils/blogUtils';

export interface PostHeaderCardProps {
  post: WP_REST_API_Post;
  sortedCategories: {
    parent: WP_REST_API_Categories;
    children: WP_REST_API_Categories;
  };
}

const hiddenCategories = [
  'uncategorized',
  'uncategorised',
  'oculto',
  'sin-categorizar',
  'all',
  'todo-el-blog'
] as const;

function getPostAuthor(post: WP_REST_API_Post): WP_REST_API_User | undefined {
  const embedded = post._embedded?.author;
  return embedded?.[0] as WP_REST_API_User | undefined;
}

function getPostThumb(post: WP_REST_API_Post): string | undefined {
  const media = post._embedded?.['wp:featuredmedia']?.[0] as
    | WP_REST_API_Attachment
    | undefined;
  return media?.source_url;
}

export const PostHeaderCard: React.FC<PostHeaderCardProps> = ({
  post,
  sortedCategories
}) => {
  const { t } = useI18n('blog');
  const { locale } = useRouter();
  const author = getPostAuthor(post);
  const thumb = getPostThumb(post);
  const readDuration = computeReadTime(post.content.rendered);

  return (
    <StyledPostHeader>
      <StyledHeaderContent>
        <StyledHeaderTags>
          <CategoryBadge
            maxCategories={7}
            tagList={getPostCategoriesId(post, sortedCategories.parent) ?? []}
          />
        </StyledHeaderTags>

        <StyledHeaderTitle
          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
        />

        <StyledHeaderMetaTable>
          <AuthorEntry>
            <MetaAvatar
              src={author?.avatar_urls?.['48'] ?? LOGO_MANFRED_WHITE}
              alt={author?.name ?? ''}
            />
            <MetaText>
              <MetaTitle>{t('post.author.title')}</MetaTitle>
              <MetaValue>
                {author ? (
                  <StyledHeaderAuthorName
                    href={author.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {author.name}
                  </StyledHeaderAuthorName>
                ) : (
                  t('post.author.default')
                )}
              </MetaValue>
            </MetaText>
          </AuthorEntry>

          <PublishedEntry>
            <MetaText>
              <MetaTitle>{t('post.published')}</MetaTitle>
              <MetaValue>
                {new Date(post.date).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric'
                })}
              </MetaValue>
            </MetaText>
          </PublishedEntry>

          <UpdatedEntry>
            <MetaText>
              <MetaTitle>{t('post.updated')}</MetaTitle>
              <MetaValue>
                {new Date(post.modified).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric'
                })}
              </MetaValue>
            </MetaText>
          </UpdatedEntry>

          <ReadTimeEntry>
            <MetaText>
              <MetaTitle>{t('post.readingTime.title')}</MetaTitle>
              <MetaValue>
                {readDuration}{' '}
                <span className="not-bold">{t('post.readingTime.min')}</span>
              </MetaValue>
            </MetaText>
          </ReadTimeEntry>
        </StyledHeaderMetaTable>
      </StyledHeaderContent>

      <StyledHeaderThumb>
        <img
          src={thumb ?? LOGO_MANFRED_WHITE}
          decoding="async"
          alt=""
          role="presentation"
        />
      </StyledHeaderThumb>
    </StyledPostHeader>
  );
};

export default PostHeaderCard;
